import { useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  TextField,
  DialogActions,
  DialogContent,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import ResponsiveDialog from "../UI/ResponsiveDialog";
import { useMutation } from "@apollo/client";
import { ADD_MATCH, GET_MATCHES_BY_SEASON_ID } from "../../GraphQL/Matches.js";
import useNotification from "../../hooks/useNotification.js";
import TeamSelect from "../Team/TeamSelect.js";

const NewMatchDialog = (props) => {
  const [match, setMatch] = useState({ hasBeenPlayed: false, matchType: "LEAGUE" });
  const [homeTeam, setHomeTeam] = useState("");
  const [awayTeam, setAwayTeam] = useState("");
  const { notifySuccess } = useNotification();

  const isValid = homeTeam !== "" && awayTeam !== "" && !!(match.startDate?.trim());

  const handleMatchTypeChange = (matchType) => {
    const updatedMatch = { ...match, matchType };
    if (matchType !== "GROUP") {
      updatedMatch.groupType = null;
    }
    if (matchType !== "KNOCKOUT") {
      updatedMatch.knockoutType = null;
    }
    setMatch(updatedMatch);
  };

  const [addMatch, { loading, error }] = useMutation(ADD_MATCH, {
    variables: {
      seasonId: props.seasonId,
      homeTeamId: homeTeam,
      awayTeamId: awayTeam,
      startDate: match.startDate,
      hasBeenPlayed: match.hasBeenPlayed,
      homeScore: parseInt(match.homeScore),
      awayScore: parseInt(match.awayScore),
      matchType: match.matchType,
      groupType: match.matchType === "GROUP" ? match.groupType : null,
      knockoutType: match.matchType === "KNOCKOUT" ? match.knockoutType : null,
    },
    refetchQueries: [GET_MATCHES_BY_SEASON_ID],
    onCompleted: () => { notifySuccess("Match created."); props.setOpen(false); },
  });

  return (
    <div>
      <ResponsiveDialog
        open={props.open}
        onKeyDown={(e) => {
          if (e.key === "Enter" && isValid) {
            addMatch();
          } else if (e.key === "Escape") {
            props.setOpen(false);
          }
        }}
        title="Match"
        onClose={() => props.setOpen(false)}
      >
        <DialogContent>
          <TeamSelect
            teams={props.teams}
            team={homeTeam}
            setTeam={setHomeTeam}
          />
          <TeamSelect
            teams={props.teams}
            team={awayTeam}
            setTeam={setAwayTeam}
          />
          <TextField
            data-testid="homeScore"
            margin="dense"
            id="homeScore"
            label="Home Score"
            type="number"
            fullWidth
            variant="standard"
            onChange={(e) => {
              var value = parseInt(e.target.value);
              if (value < 0) value = 0;
              e.target.value = value;
              setMatch({ ...match, homeScore: value });
            }}
            InputProps={{ inputProps: { min: 0, max: 1 } }}
          />
          <TextField
            data-testid="awayScore"
            margin="dense"
            id="awayScore"
            label="Away Score"
            type="number"
            fullWidth
            variant="standard"
            onChange={(e) => {
              var value = parseInt(e.target.value);
              if (value < 0) value = 0;
              e.target.value = value;
              setMatch({ ...match, awayScore: value });
            }}
            InputProps={{ inputProps: { min: 0, max: 1 } }}
          />
          <TextField
            data-testid="startDate"
            autoFocus
            margin="dense"
            id="startDate"
            label="Start Date"
            type="date"
            fullWidth
            variant="standard"
            onChange={(e) => {
              setMatch({ ...match, startDate: e.target.value });
            }}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl fullWidth margin="dense" variant="standard">
            <InputLabel id="match-type-label">Match Type</InputLabel>
            <Select
              labelId="match-type-label"
              data-testid="matchType"
              id="matchType"
              value={match.matchType || "LEAGUE"}
              onChange={(e) => handleMatchTypeChange(e.target.value)}
              label="Match Type"
            >
              <MenuItem value="LEAGUE">League</MenuItem>
              <MenuItem value="GROUP">Group</MenuItem>
              <MenuItem value="KNOCKOUT">Knockout</MenuItem>
            </Select>
          </FormControl>
          {match.matchType === "GROUP" && (
            <FormControl fullWidth margin="dense" variant="standard">
              <InputLabel id="group-type-label">Group</InputLabel>
              <Select
                labelId="group-type-label"
                data-testid="groupType"
                id="groupType"
                value={match.groupType || ""}
                onChange={(e) => setMatch({ ...match, groupType: e.target.value })}
                label="Group"
              >
                {["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P"].map((letter) => (
                  <MenuItem key={letter} value={letter}>{letter}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {match.matchType === "KNOCKOUT" && (
            <FormControl fullWidth margin="dense" variant="standard">
              <InputLabel id="knockout-type-label">Knockout Stage</InputLabel>
              <Select
                labelId="knockout-type-label"
                data-testid="knockoutType"
                id="knockoutType"
                value={match.knockoutType || ""}
                onChange={(e) => setMatch({ ...match, knockoutType: e.target.value })}
                label="Knockout Stage"
              >
                <MenuItem value="LAST_32">Last 32</MenuItem>
                <MenuItem value="LAST_16">Last 16</MenuItem>
                <MenuItem value="QUARTER_FINAL">Quarter Final</MenuItem>
                <MenuItem value="SEMI_FINAL">Semi Final</MenuItem>
                <MenuItem value="FINAL">Final</MenuItem>
              </Select>
            </FormControl>
          )}
          <FormControlLabel
            label="Has Been Played?"
            control={
              <Checkbox
                data-testid="hasBeenPlayed"
                margin="dense"
                id="hasBeenPlayed"
                label="Has Been Played?"
                variant="standard"
                onChange={(e) => {
                  setMatch({ ...match, hasBeenPlayed: e.target.checked });
                }}
              />
            }
          />
        </DialogContent>
        {error && (
          <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
            {error.message}
          </Alert>
        )}
        <DialogActions>
          <Button
            data-testid="saveNewMatchDialogButton"
            variant="contained"
            onClick={() => addMatch()}
            disabled={loading || !isValid}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "inherit" }} />
            ) : (
              "Create"
            )}
          </Button>
          <Button
            data-testid="closeNewMatchDialogButton"
            onClick={() => props.setOpen(false)}
          >
            Cancel
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </div>
  );
};

export default NewMatchDialog;
